import React from 'react';
import CardBrandMark from '../payments/CardBrandMark';

// Pure display helpers for saved cards. Cards come from GET /api/cards
// (routes/cards.js): { id, scheme, masked_pan, expiry, cardholder_name,
// created_at, consent_text_version, consented_at }. The token is NEVER present.

// masked_pan is like '411111******1111' → last 4 real digits.
export function cardLast4(maskedPan) {
    const digits = String(maskedPan || '').replace(/\D/g, '');
    return digits.length >= 4 ? digits.slice(-4) : (digits || '••••');
}

// expiry is N-Genius 'YYYY-MM' (e.g. '2030-01') → 'MM/YY'. Tolerates already
// short forms ('MM/YY', 'MMYY') so the UI never shows a raw ISO string.
export function formatExpiry(expiry) {
    const s = String(expiry || '').trim();
    if (!s) return '';
    const iso = s.match(/^(\d{4})-(\d{1,2})$/);
    if (iso) return `${iso[2].padStart(2, '0')}/${iso[1].slice(-2)}`;
    const slashed = s.match(/^(\d{1,2})\/(\d{2,4})$/);
    if (slashed) return `${slashed[1].padStart(2, '0')}/${slashed[2].slice(-2)}`;
    const four = s.match(/^(\d{2})(\d{2})$/);
    if (four) return `${four[1]}/${four[2]}`;
    return s;
}

export function cardBrandLabel(scheme) {
    const key = String(scheme || '').toUpperCase().replace(/[\s_-]/g, '');
    if (key.includes('VISA')) return 'Visa';
    if (key.includes('MASTER')) return 'Mastercard';
    if (key.includes('AMERICANEXPRESS') || key === 'AMEX') return 'Amex';
    if (key.includes('DINERS')) return 'Diners';
    if (key.includes('DISCOVER')) return 'Discover';
    if (key.includes('JCB')) return 'JCB';
    if (!scheme) return 'Card';
    return String(scheme).charAt(0) + String(scheme).slice(1).toLowerCase();
}

// (brandModifier lived here: it produced a CSS modifier class for the old
// brand-coloured TEXT pill. That pill was replaced by real brand artwork on
// 2026-08-13 — SchemeBadge renders CardBrandMark now — so nothing has called it
// since, and it compiled as a warning. Deleted rather than left dangling.)

// Small brand-coloured text pill (no external assets — CSP-safe, no icon pkg).
// REAL brand artwork since 2026-08-13 (the owner): the saved-card picker, Account →
// Payment methods and the booking page now show the same marks the embedded
// checkout does, instead of a coloured text badge. Signature unchanged, so
// every existing caller upgraded at once.
export function SchemeBadge({ scheme, className = '' }) {
    return <CardBrandMark scheme={scheme} className={className} />;
}

// "Visa •••• 5940" — the one-line human label for a card.
// "Personal Visa •••• 5940" when the client named the card, else
// "Visa •••• 5940" (2026-08-13).
export function cardLabel(card) {
    if (!card) return 'Card';
    const name = String(card.nickname || '').trim() || cardBrandLabel(card.scheme);
    return `${name} •••• ${cardLast4(card.masked_pan)}`;
}
